/**
 * @typedef {object} GeocodedResponse
 * @property {number} countryId
 * @property {GeocodedDetail[]} details - иерархия гео-записей, например: [{Самарская область}, {Тольятти}, {ул. Мира}]
 * @property {{lat: number, lng: number}} geo
 * @property {boolean} isParsed - удалось ли бэкенду найти соответствующий гео-объект
 * @property {array} microDistricts
 * @property {number} regionId
 * @property {string} text
 */

/**
 * @typedef {object} GeocodedDetail
 * @property {string} geoType
 * @property {number} id
 * @property {string} fullName
 * @property {string} name
 * @property {?object} locationInfo - если регион, то в этом поле вся доп. информация по региону (кроме id)
 * @property {object} locationInfo.boundedBy
 * @property {boolean} locationInfo.hasDistricts
 * @property {boolean} locationInfo.hasHighway
 * @property {boolean} locationInfo.hasMetro
 * @property {number} locationInfo.lat
 * @property {number} locationInfo.lng
 * @property {number} locationInfo.mainTownId
 * @property {number} locationInfo.parentId
 * @property {string} locationInfo.text
 */

import _ from 'lodash';
import ymapsRequire from '../../../../serp/helpers/ymaps-reqiure.js';
import {Region, GeoItem} from '../../../../serp/classes';
import {GEO_TYPES} from '../../../../serp/constants';

angular.module('cian.geoService')
  .factory('GeoSearch', ["$q", "$log", "GeoApi", "GeoUtils", "RegionUtilsService", function ($q, $log, GeoApi, GeoUtils, RegionUtilsService) {
    function ymapsGeocode(str) {
      var def = $q.defer();
      // Пробуем получить данные от нашего кеша
      $log.debug('Trying to get data from our caching geocoder');
      ymapsGeocodeCached(str)
        .then(function(data) {
          def.resolve(data);
        })
        .catch(function() {
          $log.warn('Failed to get data via our caching geocoder');
          $log.info('Attempting to geocode address via ymaps.geocode()');
          // Если не получилось, то обращаемся напрямую к ymaps.geocode
          ymapsGeocodeDirect(str)
            .then(function(data) {
              def.resolve(data);
            }, function() {
              $log.error('Something happened while geocoding address via both: our cached and Yandex geocoder');
              def.reject('Failed to check address via both cached and Yandex geocoder');
            });
        });

      return def.promise;
    }

    function ymapsGeocodeCached(str) {
      var def = $q.defer();
      GeoApi.geocodeCached(str).success(function(data) {
        if (data && data.items && data.items.length > 0) {
          def.resolve(
            checkAddressWithPrecision(str.trim(), data.items)
          );
        } else {
          def.reject('Failed to check address via our caching geocoder');
        }
      }).error(function() {
        def.reject('Failed to check address via our caching geocoder');
      });

      return def.promise;
    }

    var requireYmaps = function (ymapsDependencies) {
      return $q(function (resolve) {
        ymapsRequire(ymapsDependencies, function () {
          return resolve(arguments);
        });
      });
    };

    function ymapsGeocodeDirect(str) {
      // https://tech.yandex.ru/maps/doc/jsapi/2.0/ref/reference/geocode-docpage/
      return requireYmaps(['geocode'])
        .then(function (deps) {
          var geocode = deps[0];
          return geocode(str);
        })
        .then(function (res) {
          var items = res.geoObjects.toArray();
          var formattedItems = [];

          for (var i = 0; i < items.length; i++) {
            formattedItems.push({
              kind: items[i].properties.get('metaDataProperty.GeocoderMetaData.kind'),
              coordinates: items[i].geometry.getCoordinates(),
              boundedBy: items[i].properties.get('boundedBy'),
              name: items[i].properties.get('name'),
              text: items[i].properties.get('text')
            });
          }

          return checkAddressWithPrecision(str.trim(), formattedItems);
        })
        .catch(function (err) {
          $log.error(err);
        });
    }

    // Проходимся по массиву с объектами и проверяем название на соответствие строке
    // Нужно для более точной выборки адреса из результатов геокодера
    function checkAddressWithPrecision(str, data) {
      const checkStr = `${GeoUtils.getCountry()}, ${str}`;

      // Считаем результат правильным, если
      const preciseItem = data.length === 1 ?
        // 1. есть только один вариант
        data[0] :
        // 2. есть вариант с таким же названием
        _.find(data, { text: checkStr });

      if (preciseItem) {
        return preciseItem
      } else {
        const selectedItem = data[0];

        /*
         * Логируем адреса, по которым не получается наверняка определить,
         * какой результат нужен пользователю
         *
         * Игнорируем метро, т.к. по нему приходит несколько результатов,
         * если одно метро есть на двух ветках (например, Киевская)
         */
        if (!/метро\s/i.test(str)) {
          window.Raven.captureException(
            new Error('Не удалось получить точный адрес от geocode'), {
              tags: { address: str },
              extra: {
                request: str,
                selected: selectedItem.text,
                data,
              },
            });
        }

        return selectedItem;
      }
    }

    /**
     * @param {GeocodedDetail} detailsObject
     * @return {Region}
     */
    function regionFromDetails (detailsObject) {
      if (detailsObject.locationInfo) {
        return new Region({
          id: detailsObject.id,
          ...detailsObject.locationInfo
        });
      }
    }

    function checkAddress(addressObj) {
      var def = $q.defer();

      var addressData = addressObj.data;
      // Для новостроек все данные подтягиваются уже во время получения suggest'а,
      // поэтому не надо делать дополнительных запросов
      if (addressData) {
        def.resolve(new GeoItem(
          GEO_TYPES.NEWOBJECT,
          addressData.id,
          addressData.text,
          RegionUtilsService.getTopRegion(addressData.regionId)
        ));
      } else {
        var addressStr = addressObj.value;
        ymapsGeocode(addressStr)
          .then(function (res) {
            var kind = res.kind,
              coordinates = res.coordinates,
              name = res.name;

            var geocode = GeoApi.geocode({
              Lat: coordinates[1],
              Lng: coordinates[0],
              Kind: kind,
              Address: GeoUtils.getCountry() + ', ' + addressStr.trim()
            });

            geocode.success(/** @param {GeocodedResponse} data */function (data) {
              const geoItemDetails = _.last(data.details);

              // Выбран регион
              if (geoItemDetails.locationInfo) {
                def.resolve(regionFromDetails(geoItemDetails));
                return;
              }

              // Если выбран обычный гео-объект, находим его родительский регион
              const parentRegion = regionFromDetails(
                _.findLast(data.details, 'locationInfo')
              );

              if (kind === 'metro') {
                if (geoItemDetails.geoType === 'Underground') {
                  def.resolve(new GeoItem(
                    GEO_TYPES.UNDERGROUND,
                    geoItemDetails.id,
                    geoItemDetails.fullName,
                    parentRegion,
                    coordinates
                  ));
                } else {
                  def.reject('Failed to check metro [ ' + name + ' ]');
                }

                return;
              }

              // Район
              if (kind === 'district' && geoItemDetails.geoType === 'District') {
                def.resolve(new GeoItem(
                  GEO_TYPES.DISTRICT,
                  geoItemDetails.id,
                  geoItemDetails.fullName,
                  parentRegion,
                  coordinates
                ));

                return;
              }

              // Микрорайон (может быть только один)
              const microDistrict = data.microDistricts && data.microDistricts[0];
              if (microDistrict && microDistrict.hasBoundaries) {
                // Если присутствует микрорайон и у него есть границы, добавляем его
                def.resolve(new GeoItem(
                  GEO_TYPES.DISTRICT,
                  microDistrict.id,
                  `${microDistrict.locationFullName}, ${microDistrict.fullName}`,
                  parentRegion,
                  coordinates
                ));

                return;
              }

              if (data.isParsed) {
                // Добавляем гео объект как есть
                def.resolve(new GeoItem(
                  geoItemDetails.geoType.toLowerCase(),
                  geoItemDetails.id,
                  GeoUtils.removeCountryFromAddress(data.text),
                  parentRegion,
                  coordinates
                ));

              } else {
                // Если гео объекта нет в нашей БД, то создаём полигон по координатам полученным от яндекса
                const polygon = GeoUtils.createPolygonObject([GeoUtils.polygonFromBounds(res.boundedBy)], addressStr);
                def.resolve(new GeoItem(
                  GEO_TYPES.POLYGON,
                  polygon.id,
                  addressStr,
                  parentRegion,
                  null,
                  polygon.geoObject
                ));
              }
            })
            .error(function() {
              def.reject('Не удалось проверить адрес');
            });
          }, function() {
            def.reject('Не удалось проверить адрес');
          });
      }

      return def.promise;
    }

    return {
      checkAddress: checkAddress
    };
  }]);



// WEBPACK FOOTER //
// ./src/components/geo-service/src/scripts/cian.geoSearch.js